/**
 * Discriminators for the `game:error` socket event.
 *
 * `game:error` is a single, shared channel the server uses for many unrelated
 * reasons (chat rate-limit, validation, server-restart broadcast, …). The
 * client's optimistic-move feature must only roll a move back when the server
 * rejected THAT move, so move-rejection errors carry this code and nothing else
 * does. A positive marker keeps new (untagged) error types safe-by-default: no
 * code → the client never treats it as a move rejection.
 */
export const MOVE_REJECTED = 'MOVE_REJECTED';

export type GameErrorCode = typeof MOVE_REJECTED;

/**
 * Esiti dell'ack di `game:resign`.
 *
 * L'abbandono era l'unica azione di partita senza risposta: se la stanza non
 * c'era piu', o se la partita era gia' finita mentre il giocatore era via, il
 * server si limitava a scrivere una riga di log. Chi premeva il bottone non
 * vedeva succedere niente e non riceveva nemmeno un errore — il difetto per cui
 * «Abbandona sembra non funzionare».
 *
 * `ALREADY_ENDED` non e' un guasto: e' la fine partita arrivata prima
 * dell'abbandono, e viaggia con il resoconto (`ended`) proprio perche' il
 * client possa disegnarla subito invece di aspettare un `game:end` che a lui
 * non arrivera' mai.
 */
export const RESIGN_ALREADY_ENDED = 'ALREADY_ENDED';
export const RESIGN_ROOM_NOT_FOUND = 'ROOM_NOT_FOUND';
export const RESIGN_NOT_IN_GAME = 'NOT_IN_GAME';
export const RESIGN_INVALID_ROOM = 'INVALID_ROOM';
export const RESIGN_AUTH_REQUIRED = 'AUTH_REQUIRED';

export type ResignErrorCode =
  | typeof RESIGN_ALREADY_ENDED
  | typeof RESIGN_ROOM_NOT_FOUND
  | typeof RESIGN_NOT_IN_GAME
  | typeof RESIGN_INVALID_ROOM
  | typeof RESIGN_AUTH_REQUIRED;

/**
 * Esiti dell'ack di `game:offer-draw`.
 *
 * Fino a ieri l'ack portava solo FRASI inglesi ('Too many draw offers',
 * 'Cannot offer draw') e i tre store le appiattivano tutte su una chiave sola:
 * a chi veniva fermato si diceva «non e' stato possibile proporre la patta»,
 * che e' vero e inutile. La frase resta in `error`, per i log; il MOTIVO
 * viaggia in `code`, che e' l'unico posto dove `codiceErrore()` lo cerca
 * (`web/src/utils/codice-errore.ts`: legge `.code`, non `.error`).
 *
 * Solo i tre motivi del freno hanno un codice loro. «C'e' gia' un'offerta
 * pendente», «la partita e' finita» e «posto non riconosciuto» restano sul
 * generico: il client li previene da se', e quando arrivano sono casi di
 * disallineamento. Per stanza assente, non-in-partita e autenticazione si
 * riusano i letterali `RESIGN_*` qui sopra — dei codici non se ne inventano di
 * nuovi quando ce n'e' gia' uno che dice la stessa cosa.
 */
export const DRAW_MUST_MOVE = 'DRAW_MUST_MOVE';
export const DRAW_LIMIT_REACHED = 'DRAW_LIMIT_REACHED';
export const DRAW_TOO_FAST = 'DRAW_TOO_FAST';

export type DrawOfferErrorCode =
  | typeof DRAW_MUST_MOVE
  | typeof DRAW_LIMIT_REACHED
  | typeof DRAW_TOO_FAST;
